// client/src/pages/Dashboard.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useReadingData } from '../context/ReadingDataContext';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './Dashboard.css';

// FIXED: Use actual hex values instead of CSS variables in JavaScript
const NIGHT_OWL_ACCENT_1 = "#7e57c2"; // Purple
const NIGHT_OWL_ACCENT_2 = "#82aaff"; // Blue

// FIXED: Function to get CSS variable values at runtime
// This function might become less relevant for card styles if hardcoded in JS,
// but retained for other dynamic styles like chart elements.
const getCSSVariable = (variableName) => {
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
};

// Theme style objects
const darkThemeStyles = {
  backgroundColor: '#2d3748', // --night-owl-card-background
  color: '#f7fafc',           // --night-owl-text
  border: '1px solid #4a5568' // --night-owl-border
};

const lightThemeStyles = {
  backgroundColor: '#F8F9FA', // --light-card-background
  color: '#212529',           // --light-text
  border: '1px solid #DEE2E6' // --light-border
};

const Dashboard = () => {
  const { stats, readingData, loading, error } = useReadingData();

  // Determine current year as soon as the component renders so it's available
  // for other calculations
  const currentYear = new Date().getFullYear().toString();
  const previousYear = (parseInt(currentYear) - 1).toString();

  const readThisYear = (stats.readingByYear || {})[currentYear] || 0;

  // Log key stats only when values change to avoid console spam
  React.useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      typeof stats.totalBooks === 'number' &&
      typeof readThisYear === 'number'
    ) {
      console.log('Total books:', stats.totalBooks);
      console.log(`Read in ${currentYear}:`, readThisYear);
    }
  }, [stats.totalBooks, readThisYear, currentYear]);
  

  // Function to determine active theme styles
  const getActiveThemeStyles = () => {
    const isLight = document.body.classList.contains('light-theme');

    // Prioritize body class if it's set and potentially out of sync with localStorage
    // This can happen if Header.js changes class but localStorage update hasn't propagated or isn't used by Header.js
    if (isLight) {
        // If body is light, always use light theme styles
        return lightThemeStyles;
    } else {
        // If body is not light (i.e., dark), or no theme class is set, use dark theme styles
        // This also covers the initial state where localStorage might be 'light' but body class isn't updated yet.
        // However, the MutationObserver should quickly sync this.
        return darkThemeStyles;
    }
    // Fallback based on localStorage if body class logic isn't definitive (e.g. initial render before observer)
    // This part of logic might be redundant if observer works quickly.
    // return (currentTheme === 'light') ? lightThemeStyles : darkThemeStyles;
  };

  const [cardStyles, setCardStyles] = React.useState(getActiveThemeStyles());

  React.useEffect(() => {
    const updateStyles = () => {
      setCardStyles(getActiveThemeStyles());
    };

    // Initial set
    updateStyles();

    // Listen for localStorage changes (e.g., from other tabs)
    window.addEventListener('storage', updateStyles);

    // Observe body class changes (e.g., from Header component in the same window)
    const observer = new MutationObserver((mutationsList) => {
      for (let mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          updateStyles();
          break;
        }
      }
    });
    observer.observe(document.body, { attributes: true });

    return () => {
      window.removeEventListener('storage', updateStyles);
      observer.disconnect();
    };
  }, []); // Empty dependency array: runs once on mount, cleans up on unmount
  
  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <h2>Error Loading Data</h2>
        <p>{error}</p>
        <Link to="/upload" className="refresh-link">Try Again</Link>
      </div>
    );
  }
  
  if (!stats || !readingData) {
    return (
      <div className="no-data">
        <h2>No Reading Data Available</h2>
        <p>Please upload your Goodreads export file to get started.</p>
        {/* FIXED: Removed inline style */}
        <Link to="/upload" className="btn btn-primary">Upload File</Link>
      </div>
    );
  }
  
  // Safe accessors — never mutate context state directly
  const readingPace = stats.readingPace || { booksPerMonth: 0, booksPerYear: 0, pagesPerDay: 0 };
  const pageStats = stats.pageStats || { totalPages: 0, averageLength: 0, longestBook: { title: '', pages: 0 } };
  const topAuthors = stats.topAuthors || [];
  const readingByYear = stats.readingByYear || {};
  const readingByMonth = stats.readingByMonth || {};
  const ratingDistribution = stats.ratingDistribution || {};
  const readingByGenre = stats.readingByGenre || {};

  // Prepare data for charts

  const yearlyData = Object.entries(readingByYear)
    .map(([year, count]) => ({ year, count }))
    .sort((a, b) => a.year.localeCompare(b.year));
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthlyComparisonData = monthNames.map((month, index) => ({
    month,
    [currentYear]: readingByMonth[currentYear]?.[index] || 0,
    [previousYear]: readingByMonth[previousYear]?.[index] || 0
  }));

  const ratingData = Object.entries(ratingDistribution)
    .map(([rating, count]) => ({ rating: parseInt(rating), count }))
    .filter(item => item.count > 0 && item.rating > 0);

  // Updated RATING_COLORS for Night Owl theme
  const RATING_COLORS = [
    '#ff5370', // 1 star (a bit more vibrant red)
    '#ff8c42', // 2 stars (orange)
    '#ffc700', // 3 stars (yellow)
    '#c3e88d', // 4 stars (light green)
    '#89ddff'  // 5 stars (light blue)
  ];

  const safeData = Array.isArray(readingData) ? readingData : [];
  // Sort by dateRead descending so the most recently read books appear first
  const recentBooks = [...safeData]
    .filter(b => b.dateRead)
    .sort((a, b) => new Date(b.dateRead) - new Date(a.dateRead))
    .slice(0, 10);

 

  
  const tickFormatter = (value) => {
    if (typeof value === 'number' && value > 1000) {
      return `${(value / 1000).toFixed(0)}k`;
    }
    return value;
  };

  // FIXED: Get current theme colors at runtime
  const currentCardBg = getCSSVariable('--current-card-background') || '#2d3748';
  const currentBorder = getCSSVariable('--current-border') || '#4a5568';
  const currentText = getCSSVariable('--current-text') || '#f7fafc';

  return (
    <div className="dashboard">
      <h1>Reading Dashboard</h1>
      
      {/* Grouping Key Statistics */}
      <div className="stats-group-container" style={cardStyles}>
        <h2>Key Statistics</h2>
        <div className="stats-overview">
          <div className="stat-card books-read" style={cardStyles}>
            <h3>Total Books</h3>
            <div className="stat-value">{stats.totalBooks || 0}</div>
          </div>
          <div className="stat-card this-year" style={cardStyles}>
            <h3>Read in {currentYear}</h3>
            <div className="stat-value">{readingByYear[currentYear] || 0}</div>
          </div>
          <div className="stat-card rating" style={cardStyles}>
            <h3>Average Rating</h3>
            <div className="stat-value">{(stats.averageRating || 0).toFixed(1)}</div>
            <div className="rating-stars">
              {'★'.repeat(Math.round(stats.averageRating || 0))}
              {'☆'.repeat(5 - Math.round(stats.averageRating || 0))}
            </div>
          </div>
          <div className="stat-card reading-pace" style={cardStyles}>
            <h3>Books/Month</h3>
            <div className="stat-value">
              {(readingPace.booksPerMonth || 0).toFixed(1)}
            </div>
          </div>
          <div className="stat-card" style={cardStyles}>
            <h3>Pages/Day</h3>
            <div className="stat-value">{(readingPace.pagesPerDay || 0).toFixed(1)}</div>
          </div>
          <div className="stat-card" style={cardStyles}>
            <h3>Avg. Length</h3>
            <div className="stat-value">{Math.round(pageStats.averageLength || 0)}</div>
          </div>
        </div>
      </div>
      
      {/* Charts remain in their own top-level sections */}
      <div className="chart-card yearly-chart">
        <h3>Books Read By Year</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={yearlyData} margin={{ top: 5, right: 0, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={currentBorder} />
            <XAxis dataKey="year" tick={{ fill: currentText, fontSize: 12 }} />
            <YAxis tick={{ fill: currentText, fontSize: 12 }} tickFormatter={tickFormatter}/>
            <Tooltip 
              contentStyle={{ backgroundColor: currentCardBg, border: `1px solid ${currentBorder}`, color: currentText, borderRadius: '6px' }} 
              itemStyle={{ color: currentText }}
              cursor={{ fill: 'rgba(130, 170, 255, 0.15)' }}/>
            <Bar dataKey="count" fill={NIGHT_OWL_ACCENT_1} name="Books Read" isAnimationActive={true} />
          </BarChart>
        </ResponsiveContainer>
      </div>
        
      <div className="chart-card monthly-chart">
        <h3>Monthly Comparison ({currentYear} vs {previousYear})</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={monthlyComparisonData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }} isAnimationActive={true}>
            <CartesianGrid strokeDasharray="3 3" stroke={currentBorder} />
            <XAxis dataKey="month" tick={{ fill: currentText, fontSize: 12 }} />
            <YAxis tick={{ fill: currentText, fontSize: 12 }} tickFormatter={tickFormatter}/>
            <Tooltip 
              contentStyle={{ backgroundColor: currentCardBg, border: `1px solid ${currentBorder}`, color: currentText, borderRadius: '6px' }} 
              itemStyle={{ color: currentText }}
              cursor={{ fill: 'rgba(130, 170, 255, 0.15)' }}/>
            <Legend wrapperStyle={{ color: currentText, fontSize: '12px' }} />
            <Bar dataKey={previousYear} fill={NIGHT_OWL_ACCENT_2} name={previousYear} isAnimationActive={true} />
            <Bar dataKey={currentYear} fill={NIGHT_OWL_ACCENT_1} name={currentYear} isAnimationActive={true} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Grouping Rating Distribution and Top Authors */}
      <div className="charts-container">
        <div className="chart-card rating-chart">
          <h3>Rating Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <Pie
                data={ratingData}
                cx="50%"
                cy="50%"
                labelLine={{ stroke: currentText }}
                outerRadius={80}
                dataKey="count"
                nameKey="rating"
                label={({rating, percent, count}) => `${rating}★: ${(percent * 100).toFixed(0)}% (${count})`}
                tick={{ fill: currentText }}
                isAnimationActive={true}
              >
                {ratingData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={RATING_COLORS[(entry.rating - 1) % RATING_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ backgroundColor: currentCardBg, border: `1px solid ${currentBorder}`, color: currentText, borderRadius: '6px' }} 
                itemStyle={{ color: currentText }}
                formatter={(value, name) => [`${value} books`, `${name} stars`]}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        <div className="chart-card authors-list-card">
          <h3>Top Authors</h3>
          <ul className="top-authors">
            {topAuthors.slice(0, 5).map((author, index) => (
              <li key={index} className="author-item">
                <span className="author-name">{author.author}</span>
                <span className="author-count">{author.count} books</span>
                <div className="author-bar">
                  <div
                    className="author-bar-fill"
                    style={{
                      width: `${(author.count / (topAuthors[0]?.count || 1)) * 100}%`,
                      backgroundColor: index % 2 === 0 ? NIGHT_OWL_ACCENT_1 : NIGHT_OWL_ACCENT_2
                    }}
                  ></div>
                </div>
              </li>
            ))}
          </ul>
          {topAuthors.length > 5 &&
            <Link to="/top-authors" className="see-more-link">See More Authors</Link>}
        </div>
      </div>
      
      <div className="recent-books">
        <h2>Recently Read Books</h2>
        <div className="books-grid">
          {recentBooks.map((book, index) => (
            <div key={index} className="book-card">
              <h3 className="book-title" title={book.title}>{book.title}</h3>
              <p className="book-author">by {book.author}</p>
              <div className="book-rating">
                {'★'.repeat(book.myRating || 0)}
                {'☆'.repeat(5 - (book.myRating || 0))}
              </div>
              <p className="book-date">
                Read: {book.dateRead ? new Date(book.dateRead).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          ))}
        </div>
        {safeData.length > 10 && 
          <Link to="/recent-books" className="see-more-link">See All Recent Books</Link>}
      </div>
      
      <div className="reading-insights">
        <h2>Reading Insights</h2>
        <div className="insights-grid">
          <div className="insight-card">
            <h3>Total Pages</h3>
            <p>{(pageStats.totalPages || stats.totalPages || 0).toLocaleString()}</p>
          </div>
          <div className="insight-card">
            <h3>Avg. Pages/Book</h3>
            <p>{(pageStats.averageLength || 0).toFixed(0)}</p>
          </div>
          <div className="insight-card">
            <h3>Books/Year</h3>
            <p>{(readingPace.booksPerYear || 0).toFixed(1)}</p>
          </div>
          <div className="insight-card">
            <h3>Longest Book</h3>
            <p title={pageStats.longestBook?.title || ''}>
              {(pageStats.longestBook?.title || '').length > 25
                ? pageStats.longestBook.title.substring(0, 22) + '...'
                : (pageStats.longestBook?.title || 'N/A')}
            </p>
            <p>{(pageStats.longestBook?.pages || 0).toLocaleString()} pages</p>
          </div>
          <div className="insight-card">
            <h3>Most Read Genre</h3>
            <p>{Object.entries(readingByGenre).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}</p>
          </div>
          <div className="insight-card">
            <h3>Favorite Author</h3>
            <p>{topAuthors[0]?.author || 'N/A'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
